export type PrimaryRecordConditionStatus = 'active' | 'inactive' | 'chronic' | 'ruled-out';

export type PrimaryRecordAllergyStatus = PrimaryRecordConditionStatus;

export type PrimaryRecordAllergyType = 'food' | 'drug' | 'environment';

export type PrimaryRecordAllergyReactionType = 'allergic' | 'intolerant' | 'avoidance';

export type PrimaryRecordVisitType = 'routine' | 'walk-in' | 'checkup' | 'followup' | 'emergency' | 'other';

export type PrimaryRecordVisitRecurrenceFrequency = 'daily' | 'weekly';

/** Recurrence rule stamped on every occurrence of a recurring visit series. */
export type PrimaryRecordVisitRecurrence = {
    frequency: PrimaryRecordVisitRecurrenceFrequency;
    /** Weekly only: 0 (Sunday) - 6 (Saturday). */
    weekdays?: number[] | null;
    /** Last calendar day (inclusive, yyyy-MM-dd) the series generates occurrences for. Required — no unbounded series. */
    endDate: string;
    /** IANA zone the series was authored in — server-side expansion keeps wall-clock times across DST. */
    timeZone?: string | null;
};

export type VisitDuration = 15 | 30 | 45 | 60 | 90 | 120;

export type Coding = {
    system: string;
    code: string;
    display: string;
};

export type ResourceClinicalImpression = {
    title: string;
    summary: string;
};

export type PrimaryRecordMedicationFrequency =
    | 'daily'
    | '2-daily'
    | '3-daily'
    | 'weekly'
    | 'monthly'
    | 'continuously'
    | 'other';

export type PrimaryRecordCondition = {
    version: '1';
    id: string;
    type: 'condition';
    name: string;
    startDateTime?: string;
    endDateTime?: string;
    status: PrimaryRecordConditionStatus;
    notes: string;
};

export type PrimaryRecordAllergy = {
    version: '1';
    id: string;
    type: 'allergy';
    name: string;
    startDateTime?: string;
    endDateTime?: string;
    status: PrimaryRecordConditionStatus;
    allergyType: PrimaryRecordAllergyType | null;
    reactionType?: PrimaryRecordAllergyReactionType | null;
    notes: string;
};

export type PrimaryRecordMedication = {
    version: '1';
    id: string;
    type: 'medication';
    name: string;
    fdaName: string | null;
    genericNameOrActiveIngredient: string | null;
    strength: string | null;
    form: string | null;
    route: string | null;
    quantity: string | null;
    asNeeded: boolean | null;
    frequency: PrimaryRecordMedicationFrequency | null;
    isActive: boolean;
    whenToTake: string | null;
    numberOfRefills: string | null;
    lastRefillDateTime: string | null;
    providerCareTeamMemberId: string | null;
    providerName: string | null;
    startDateTime?: string;
    endDateTime?: string;
    notes: string;
};

export type PrimaryRecordVisit = {
    version: '1';
    id: string;
    type: 'visit';
    visitType: PrimaryRecordVisitType | null;
    /** Only populated when visitType is 'other' — replaces the type in the visit's display name. */
    visitTypeOtherLabel?: string | null;
    /** Links every occurrence of one recurring series. */
    recurrenceGroupId?: string | null;
    /** The series rule; carried on each occurrence for display and series operations. */
    recurrence?: PrimaryRecordVisitRecurrence | null;
    reason: string | null;
    startDateTime?: string;
    duration: VisitDuration | null;
    endDateTime?: string;
    notes: string | null;
    providerCareTeamMemberId: string | null;
    providerName: string | null;
    address1: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    clinicalImpressions?: ResourceClinicalImpression[];
};

export type PrimaryRecordVaccination = {
    version: '1';
    id: string;
    type: 'vaccination';
    vaccineType: Coding;
    dateTime: string | null;
    notes: string | null;
    address1: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
};

export type PrimaryRecordProcedure = {
    version: '1';
    id: string;
    type: 'procedure';
    name: string;
    dateTime: string | null;
    notes: string;
    providerCareTeamMemberId: string | null;
    providerName: string | null;
    address1: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    results: string | null;
};

export type PrimaryRecordImaging = {
    version: '1';
    id: string;
    type: 'imaging';
    name: string;
    dateTime: string | null;
    notes: string;
    address1: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    results: string | null;
};

export type Quantity = fhir2.Quantity | fhir3.Quantity | fhir4.Quantity;
export type CodeableConcept = fhir2.CodeableConcept | fhir3.CodeableConcept | fhir4.CodeableConcept;
export type FhirCoding = fhir2.Coding | fhir3.Coding | fhir4.Coding;

export type QuantityValue = {
    type: 'quantity';
    valueQuantity: Quantity;
};

export type CodeableConceptValue = {
    type: 'codeableConcept';
    valueCodeableConcept: CodeableConcept;
};

export type StringValue = {
    type: 'string';
    valueString: string;
};

export type LabValue<TValue = QuantityValue | CodeableConceptValue | StringValue> = {
    componentCoding?: Coding;
    value: TValue;
    refLow?: Quantity;
    refHigh?: Quantity;
};

export type PrimaryRecordLab = {
    version: '1';
    id: string;
    type: 'lab';
    labType: Coding;
    dateTime: string | null;
    values: LabValue[];
};

export type PrimaryRecordResource =
    | PrimaryRecordCondition
    | PrimaryRecordAllergy
    | PrimaryRecordMedication
    | PrimaryRecordVisit
    | PrimaryRecordVaccination
    | PrimaryRecordProcedure
    | PrimaryRecordImaging
    | PrimaryRecordLab;

// Labs cannot be user-edited
export type EditablePrimaryRecordResource =
    | PrimaryRecordCondition
    | PrimaryRecordAllergy
    | PrimaryRecordMedication
    | PrimaryRecordVisit
    | PrimaryRecordVaccination
    | PrimaryRecordProcedure
    | PrimaryRecordImaging;

export type EditablePrimaryRecordResourceWithoutId =
    | Omit<PrimaryRecordCondition, 'id'>
    | Omit<PrimaryRecordAllergy, 'id'>
    | Omit<PrimaryRecordMedication, 'id'>
    | Omit<PrimaryRecordVisit, 'id'>
    | Omit<PrimaryRecordVaccination, 'id'>
    | Omit<PrimaryRecordProcedure, 'id'>
    | Omit<PrimaryRecordImaging, 'id'>;

export type FhirVersionResourceMap<
    DSTU2Element extends fhir2.BackboneElement,
    STU3Element extends fhir3.BackboneElement,
    R4Element extends fhir4.BackboneElement,
> = {
    dstu2: DSTU2Element;
    stu3: STU3Element;
    r4: R4Element;
};

export type FhirConditionVersionResourceMap = FhirVersionResourceMap<fhir2.Condition, fhir3.Condition, fhir4.Condition>;
export type FhirAllergyIntoleranceVersionResourceMap = FhirVersionResourceMap<
    fhir2.AllergyIntolerance,
    fhir3.AllergyIntolerance,
    fhir4.AllergyIntolerance
>;
export type FhirMedicationVersionResourceMap = FhirVersionResourceMap<
    fhir2.MedicationOrder,
    fhir3.MedicationRequest,
    fhir4.MedicationRequest
>;
export type FhirMedicationDefinitionVersionResourceMap = FhirVersionResourceMap<
    fhir2.Medication,
    fhir3.Medication,
    fhir4.Medication
>;
export type FhirEncounterVersionResourceMap = FhirVersionResourceMap<fhir2.Encounter, fhir3.Encounter, fhir4.Encounter>;
export type FhirImmunizationVersionResourceMap = FhirVersionResourceMap<
    fhir2.Immunization,
    fhir3.Immunization,
    fhir4.Immunization
>;
export type FhirProcedureVersionResourceMap = FhirVersionResourceMap<fhir2.Procedure, fhir3.Procedure, fhir4.Procedure>;
export type FhirImagingStudyVersionResourceMap = FhirVersionResourceMap<
    fhir2.ImagingStudy,
    fhir3.ImagingStudy,
    fhir4.ImagingStudy
>;
export type FhirObservationVersionResourceMap = FhirVersionResourceMap<
    fhir2.Observation,
    fhir3.Observation,
    fhir4.Observation
>;

// not first class resources, but referenced in the codebase
export type FhirLocationVersionResourceMap = FhirVersionResourceMap<fhir2.Location, fhir3.Location, fhir4.Location>;
export type FhirDiagnosticReportVersionResourceMap = FhirVersionResourceMap<
    fhir2.DiagnosticReport,
    fhir3.DiagnosticReport,
    fhir4.DiagnosticReport
>;
export type FhirPatientVersionResourceMap = FhirVersionResourceMap<fhir2.Patient, fhir3.Patient, fhir4.Patient>;
export type FhirClinicalImpressionVersionResourceMap = FhirVersionResourceMap<
    fhir2.ClinicalImpression,
    fhir3.ClinicalImpression,
    fhir4.ClinicalImpression
>;

export type FhirAudiEventResourceMap = FhirVersionResourceMap<fhir2.AuditEvent, fhir3.AuditEvent, fhir4.AuditEvent>;

export type AllVersions<
    ResourceMap extends FhirVersionResourceMap<fhir2.BackboneElement, fhir3.BackboneElement, fhir4.BackboneElement>,
> = ResourceMap['dstu2'] | ResourceMap['stu3'] | ResourceMap['r4'];

export type FhirCondition = AllVersions<FhirConditionVersionResourceMap>;
export type FhirAllergyIntolerance = AllVersions<FhirAllergyIntoleranceVersionResourceMap>;
export type FhirMedication = AllVersions<FhirMedicationVersionResourceMap>;
export type FhirMedicationDefinition = AllVersions<FhirMedicationDefinitionVersionResourceMap>;
export type FhirEncounter = AllVersions<FhirEncounterVersionResourceMap>;
export type FhirImmunization = AllVersions<FhirImmunizationVersionResourceMap>;
export type FhirProcedure = AllVersions<FhirProcedureVersionResourceMap>;
export type FhirImagingStudy = AllVersions<FhirImagingStudyVersionResourceMap>;
export type FhirLocation = AllVersions<FhirLocationVersionResourceMap>;
export type FhirDiagnosticReport = AllVersions<FhirDiagnosticReportVersionResourceMap>;
export type FhirPatient = AllVersions<FhirPatientVersionResourceMap>;
export type FhirAuditEvent = AllVersions<FhirAudiEventResourceMap>;
export type FhirObservation = AllVersions<FhirObservationVersionResourceMap>;
export type FhirClinicalImpression = AllVersions<FhirClinicalImpressionVersionResourceMap>;

export type AllIngestedOneUpResourceTypeMaps =
    | FhirConditionVersionResourceMap
    | FhirAllergyIntoleranceVersionResourceMap
    | FhirMedicationVersionResourceMap
    | FhirMedicationDefinitionVersionResourceMap
    | FhirEncounterVersionResourceMap
    | FhirImmunizationVersionResourceMap
    | FhirProcedureVersionResourceMap
    | FhirImagingStudyVersionResourceMap
    | FhirLocationVersionResourceMap
    | FhirDiagnosticReportVersionResourceMap
    | FhirPatientVersionResourceMap
    | FhirAllergyIntoleranceVersionResourceMap
    | FhirObservationVersionResourceMap
    | FhirClinicalImpressionVersionResourceMap;

/**
 * Fhir resources that are directly mapped to primary record resources
 */
export type SupportedFhirResources =
    | FhirCondition
    | FhirAllergyIntolerance
    | FhirMedication
    | FhirEncounter
    | FhirImmunization
    | FhirProcedure
    | FhirImagingStudy
    | FhirObservation;

export type ReferencedFhirResources =
    | FhirLocation
    | FhirDiagnosticReport
    | FhirPatient
    | FhirClinicalImpression
    | FhirMedicationDefinition;

export type AllIngestedOneUpHealthResources = SupportedFhirResources | ReferencedFhirResources | FhirAuditEvent;

export type AllIngestedOneUpHealthResourceTypes = AllIngestedOneUpHealthResources['resourceType'];

export type FhirResourceWithSource<TResource extends SupportedFhirResources> = TResource & {
    source: '1uphealth';
};

export type OneUpHealthResource<TResource extends SupportedFhirResources = SupportedFhirResources> = TResource & {
    source: '1uphealth';
};

export type Resource<TResource extends PrimaryRecordOrOneUpResource = PrimaryRecordOrOneUpResource> =
    TResource extends PrimaryRecordResource
        ? TResource
        : never | TResource extends SupportedFhirResources
          ? OneUpHealthResource<TResource>
          : never;

export type AnyFhirDomainResource = fhir2.DomainResource | fhir3.DomainResource | fhir4.DomainResource;

export type VersionedFhirResource<TResource extends AnyFhirDomainResource> = {
    resource: TResource;
    fhirVersion: SupportedFhirVersions;
};

export type PrimaryRecordOrOneUpResource = PrimaryRecordResource | SupportedFhirResources;

/**
 * Resolved FHIR resource references for any EHR source (1up, Fasten, etc).
 * Making these types as arrays in case we need to support multiple references in the future, but
 * they will only have 0 or 1 elements for now.
 */
export type ResourceReferences = {
    diagnosticReports?: VersionedFhirResource<FhirDiagnosticReport>[];
    procedures?: VersionedFhirResource<FhirProcedure>[];
    locations?: VersionedFhirResource<FhirLocation>[];
    encounters?: VersionedFhirResource<FhirEncounter>[];
    clinicalImpressions?: VersionedFhirResource<FhirClinicalImpression>[];
    medications?: VersionedFhirResource<FhirMedicationDefinition>[];
};

export type PrimaryRecordAppResourceMetadata = {
    source: 'primary-record-app';
    createdByUserId: string;
    createdDateTime: string;
};

export type OneUpHealthResourceMetadata = {
    source: '1uphealth';
    healthSystemId: number | null;
    healthSystemName: string | null;
    ingestDateTime: string;
    fhirVersion: SupportedFhirVersions;
    references?: ResourceReferences;
};

export type FastenResourceMetadata = {
    source: 'fasten';
    orgConnectionId: string;
    platformName: string | null;
    institutionName: string | null;
    ingestDateTime: string;
    fhirVersion: SupportedFhirVersions;
    references?: ResourceReferences;
};

export type ResourceMetadata<TResource extends PrimaryRecordOrOneUpResource = PrimaryRecordOrOneUpResource> =
    TResource extends PrimaryRecordResource
        ? PrimaryRecordAppResourceMetadata
        : never | TResource extends SupportedFhirResources
          ? OneUpHealthResourceMetadata | FastenResourceMetadata
          : never;

export type PrimaryRecordResourceCategory<
    TResource extends PrimaryRecordOrOneUpResource = PrimaryRecordOrOneUpResource,
> = TResource extends FhirCondition | PrimaryRecordCondition
    ? 'condition'
    : TResource extends FhirAllergyIntolerance | PrimaryRecordAllergy
      ? 'allergy'
      : TResource extends FhirMedication | PrimaryRecordMedication
        ? 'medication'
        : TResource extends FhirEncounter | PrimaryRecordVisit
          ? 'visit'
          : TResource extends FhirImmunization | PrimaryRecordVaccination
            ? 'vaccination'
            : TResource extends FhirProcedure | PrimaryRecordProcedure
              ? 'procedure'
              : TResource extends FhirImagingStudy | PrimaryRecordImaging
                ? 'imaging'
                : TResource extends FhirObservation | PrimaryRecordLab
                  ? 'lab'
                  : never;

export type MappedPrimaryRecordResourceCategory<TResource extends PrimaryRecordOrOneUpResource> =
    TResource extends PrimaryRecordResource
        ? TResource
        : TResource extends FhirCondition
          ? PrimaryRecordCondition
          : TResource extends FhirAllergyIntolerance
            ? PrimaryRecordAllergy
            : TResource extends FhirMedication
              ? PrimaryRecordMedication
              : TResource extends FhirEncounter
                ? PrimaryRecordVisit
                : TResource extends FhirImmunization
                  ? PrimaryRecordVaccination
                  : TResource extends FhirProcedure
                    ? PrimaryRecordProcedure
                    : TResource extends FhirImagingStudy
                      ? PrimaryRecordImaging
                      : TResource extends FhirObservation
                        ? PrimaryRecordLab
                        : never;

export type UpdateMetadata = {
    updatedByUserId: string;
    updatedDateTime: string;
};

export type SupportedFhirResourceTypes =
    | 'Condition'
    | 'AllergyIntolerance'
    | 'MedicationRequest'
    | 'MedicationOrder'
    | 'Encounter'
    | 'Immunization'
    | 'Procedure'
    | 'ImagingStudy'
    | 'Observation';

export type SupportedFhirVersions = 'dstu2' | 'stu3' | 'r4';

export type OneUpHealthResponseEntry<TResource = AllIngestedOneUpHealthResources> = {
    resource: TResource;
    fullUrl: string;
};

export type OneUpHealthSupportedResourceType =
    | 'Condition'
    | 'AllergyIntolerance'
    | 'MedicationRequest'
    | 'MedicationOrder'
    | 'Encounter'
    | 'Immunization'
    | 'Procedure'
    | 'ImagingStudy'
    | 'Observation';

export type AllResources = fhir2.Resource | fhir3.Resource | fhir4.Resource;

export const resourceRecordCategoryTypeGuards = {
    isCondition: (resource: PrimaryRecordResource): resource is PrimaryRecordCondition => {
        return resource.type === 'condition';
    },
    isAllergy: (resource: PrimaryRecordResource): resource is PrimaryRecordAllergy => {
        return resource.type === 'allergy';
    },
    isMedication: (resource: PrimaryRecordResource): resource is PrimaryRecordMedication => {
        return resource.type === 'medication';
    },
    isVisit: (resource: PrimaryRecordResource): resource is PrimaryRecordVisit => {
        return resource.type === 'visit';
    },
    isVaccination: (resource: PrimaryRecordResource): resource is PrimaryRecordVaccination => {
        return resource.type === 'vaccination';
    },
    isProcedure: (resource: PrimaryRecordResource): resource is PrimaryRecordProcedure => {
        return resource.type === 'procedure';
    },
    isImaging: (resource: PrimaryRecordResource): resource is PrimaryRecordImaging => {
        return resource.type === 'imaging';
    },
    isLab: (resource: PrimaryRecordResource): resource is PrimaryRecordLab => {
        return resource.type === 'lab';
    },
};
